/**
 * QuickDelivery Pro — translations for the app shell.
 * French is the working language of Moroccan distribution; Arabic is the
 * customer-facing one; English exists for the store listing and support.
 */

import type { Language } from './domain';

export const DAY_LABELS: Record<Language, string[]> = {
  fr: ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche'],
  ar: ['الإثنين', 'الثلاثاء', 'الأربعاء', 'الخميس', 'الجمعة', 'السبت', 'الأحد'],
  en: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
};

export const DAY_SHORT: Record<Language, string[]> = {
  fr: ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'],
  ar: ['ن', 'ث', 'ر', 'خ', 'ج', 'س', 'ح'],
  en: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
};

export const LOCALES: Record<Language, string> = { fr: 'fr-MA', ar: 'ar-MA', en: 'en-GB' };
export const RTL: Record<Language, boolean> = { fr: false, ar: true, en: false };

const fr = {
  appName: 'QuickDelivery Pro',
  dashboard: 'Accueil', newOrder: 'Commander', products: 'Produits', clients: 'Clients',
  history: 'Historique', analytics: 'Analytiques', settings: 'Réglages',

  todaySales: 'Ventes du jour', todayDeliveries: 'Livraisons', startOrder: 'Nouvelle Commande',
  recentDeliveries: 'Dernières Ventes', noOrders: 'Aucune donnée.', weekSales: 'Cette semaine',
  outstanding: 'Impayé', collected: 'Encaissé',

  selectClient: 'Client', allClients: 'Tous les clients', todayClients: 'Programme du jour',
  passingClient: 'Client de passage', instantTotal: 'Total', cancel: 'Annuler',
  saveOrder: 'Valider', editOrder: 'Modifier la commande', search: 'Rechercher…',
  amountPaid: 'Montant encaissé', fullyPaid: 'Payé intégralement', partialPayment: 'Paiement partiel',
  note: 'Note',

  addProduct: 'Nouveau Produit', productName: 'Désignation', unitPrice: 'Prix (DH)',
  saveProduct: 'Enregistrer', category: 'Catégorie', archive: 'Archiver', archived: 'Archivés',
  restore: 'Restaurer',

  registerClient: 'Nouveau Client', shopName: 'Nom / Enseigne', city: 'Ville', sector: 'Secteur',
  deliveryDays: 'Planning', saveClient: 'Confirmer', phone: 'Téléphone', address: 'Adresse',
  call: 'Appeler',

  orderHistory: 'Historique', items: 'articles', orderDetails: 'Contenu', edit: 'Modifier',
  delete: 'Supprimer', saveChanges: 'Enregistrer les modifications',
  confirmDelete: 'Supprimer définitivement cette commande ?',

  lifetimeTotal: "Chiffre d'Affaires", avgOrder: 'Panier Moyen', dailyRevenue: 'Ventes Hebdo',
  totalClients: 'Clients actifs', totalOrders: 'Commandes', performance: 'Performance',
  topSectors: 'Ventes par Secteur', topProducts: 'Top Produits', topClientsTitle: 'Meilleurs Clients',
  units: 'unités', ordersLabel: 'commandes', unknown: 'Inconnu',

  currency: 'DH', uploadImage: 'Photo', filterBy: 'Filtrer par',
  noClientToday: 'Aucun client prévu aujourd’hui.', empty: 'Rien à afficher.',
  confirm: 'Confirmer', close: 'Fermer', save: 'Enregistrer',

  exportCsv: 'Exporter en CSV', backup: 'Sauvegarde', restoreBackup: 'Restaurer une sauvegarde',
  backupDone: 'Sauvegarde créée.', restoreDone: 'Données restaurées.',
  restoreWarning: 'Cela remplacera toutes les données de cet appareil. Continuer ?',
  language: 'Langue', dataStoredLocally: 'Vos données restent sur cet appareil.',

  proFeature: 'Fonctionnalité Pro', upgrade: 'Passer à Pro', upgradeNow: 'Débloquer',
  trialBanner: 'Essai Pro — {n} jours restants', trialLastDay: 'Dernier jour d’essai Pro',
  freePlan: 'Plan Gratuit', proPlan: 'QuickDelivery Pro',
  limitReached: 'Limite du plan gratuit atteinte',
  limitClients: 'Le plan gratuit est limité à {n} clients.',
  limitProducts: 'Le plan gratuit est limité à {n} produits.',
  limitOrders: 'Le plan gratuit est limité à {n} commandes par mois.',
  upgradeBlurb: 'Passez à Pro pour un usage illimité, les analyses complètes, l’export et les sauvegardes.',

  activation: 'Activation', licenseKey: 'Clé de licence', activateApp: 'Activer',
  activating: 'Activation…', activationOk: 'Licence activée. Merci !',
  activationBadKey: 'Clé invalide ou déjà utilisée sur trop d’appareils.',
  activationOffline: 'Connexion requise pour la première activation.',
  buyLicense: 'Acheter une licence', haveKey: 'J’ai déjà une clé',
  deviceId: 'ID de l’appareil', licenseActive: 'Licence active', licenseUntil: 'Valide jusqu’au {d}',
  licenseLifetime: 'Licence à vie', deactivate: 'Désactiver cet appareil',
  licenseExpired: 'Votre abonnement a expiré. Renouvelez pour continuer.',
  licenseWrongDevice: 'Cette licence est activée sur un autre appareil.',

  byVendor: 'Un logiciel SaaS de',
} as const;

/** Keys are fixed by the French dictionary; every value is a plain string. */
export type Dict = { [K in keyof typeof fr]: string };

const ar: Dict = {
  appName: 'التوزيع برو',
  dashboard: 'الرئيسية', newOrder: 'طلبية', products: 'المنتجات', clients: 'الزبناء',
  history: 'السجل', analytics: 'التحليلات', settings: 'الإعدادات',

  todaySales: 'مبيعات اليوم', todayDeliveries: 'التوصيلات', startOrder: 'طلبية جديدة',
  recentDeliveries: 'آخر العمليات', noOrders: 'لا توجد بيانات.', weekSales: 'هذا الأسبوع',
  outstanding: 'غير مدفوع', collected: 'المحصّل',

  selectClient: 'الزبون', allClients: 'كل الزبناء', todayClients: 'برنامج اليوم',
  passingClient: 'زبون عابر', instantTotal: 'المجموع', cancel: 'إلغاء',
  saveOrder: 'تأكيد', editOrder: 'تعديل الطلبية', search: 'بحث…',
  amountPaid: 'المبلغ المحصّل', fullyPaid: 'مدفوع بالكامل', partialPayment: 'دفع جزئي',
  note: 'ملاحظة',

  addProduct: 'منتج جديد', productName: 'اسم المنتج', unitPrice: 'الثمن (درهم)',
  saveProduct: 'حفظ', category: 'الفئة', archive: 'أرشفة', archived: 'المؤرشفة',
  restore: 'استرجاع',

  registerClient: 'زبون جديد', shopName: 'الاسم / المحل', city: 'المدينة', sector: 'القطاع',
  deliveryDays: 'البرمجة', saveClient: 'تأكيد', phone: 'الهاتف', address: 'العنوان',
  call: 'اتصال',

  orderHistory: 'السجل', items: 'منتجات', orderDetails: 'المحتوى', edit: 'تعديل',
  delete: 'حذف', saveChanges: 'حفظ التغييرات',
  confirmDelete: 'هل تريد حذف هذه الطلبية نهائيا؟',

  lifetimeTotal: 'رقم المعاملات', avgOrder: 'معدل الطلب', dailyRevenue: 'المبيعات الأسبوعية',
  totalClients: 'الزبناء النشطون', totalOrders: 'الطلبيات', performance: 'الأداء',
  topSectors: 'المبيعات حسب القطاع', topProducts: 'أفضل المنتجات', topClientsTitle: 'أفضل الزبناء',
  units: 'وحدة', ordersLabel: 'طلبية', unknown: 'غير معروف',

  currency: 'درهم', uploadImage: 'صورة', filterBy: 'تصفية حسب',
  noClientToday: 'لا يوجد زبناء مبرمجون اليوم.', empty: 'لا شيء لعرضه.',
  confirm: 'تأكيد', close: 'إغلاق', save: 'حفظ',

  exportCsv: 'تصدير CSV', backup: 'نسخة احتياطية', restoreBackup: 'استرجاع نسخة احتياطية',
  backupDone: 'تم إنشاء النسخة الاحتياطية.', restoreDone: 'تم استرجاع البيانات.',
  restoreWarning: 'سيتم استبدال كل بيانات هذا الجهاز. هل تريد المتابعة؟',
  language: 'اللغة', dataStoredLocally: 'بياناتك تبقى على هذا الجهاز.',

  proFeature: 'ميزة Pro', upgrade: 'الترقية إلى Pro', upgradeNow: 'فتح الميزة',
  trialBanner: 'تجربة Pro — {n} يوما متبقيا', trialLastDay: 'آخر يوم من تجربة Pro',
  freePlan: 'الخطة المجانية', proPlan: 'QuickDelivery Pro',
  limitReached: 'تم بلوغ حد الخطة المجانية',
  limitClients: 'الخطة المجانية محدودة في {n} زبناء.',
  limitProducts: 'الخطة المجانية محدودة في {n} منتجات.',
  limitOrders: 'الخطة المجانية محدودة في {n} طلبية في الشهر.',
  upgradeBlurb: 'رقّ إلى Pro للاستعمال غير المحدود، التحليلات الكاملة، التصدير والنسخ الاحتياطي.',

  activation: 'التفعيل', licenseKey: 'مفتاح الترخيص', activateApp: 'تفعيل',
  activating: 'جاري التفعيل…', activationOk: 'تم تفعيل الترخيص. شكرا لك!',
  activationBadKey: 'مفتاح غير صالح أو مستعمل على عدد كبير من الأجهزة.',
  activationOffline: 'يتطلب التفعيل الأول اتصالا بالإنترنت.',
  buyLicense: 'شراء ترخيص', haveKey: 'لدي مفتاح بالفعل',
  deviceId: 'معرّف الجهاز', licenseActive: 'الترخيص نشط', licenseUntil: 'صالح إلى غاية {d}',
  licenseLifetime: 'ترخيص مدى الحياة', deactivate: 'إلغاء تفعيل هذا الجهاز',
  licenseExpired: 'انتهى اشتراكك. جدّده للمتابعة.',
  licenseWrongDevice: 'هذا الترخيص مفعّل على جهاز آخر.',

  byVendor: 'برنامج SaaS من',
};

const en: Dict = {
  appName: 'QuickDelivery Pro',
  dashboard: 'Home', newOrder: 'Order', products: 'Products', clients: 'Clients',
  history: 'History', analytics: 'Analytics', settings: 'Settings',

  todaySales: "Today's sales", todayDeliveries: 'Deliveries', startOrder: 'New Order',
  recentDeliveries: 'Recent Sales', noOrders: 'No data yet.', weekSales: 'This week',
  outstanding: 'Outstanding', collected: 'Collected',

  selectClient: 'Client', allClients: 'All clients', todayClients: "Today's round",
  passingClient: 'Walk-in', instantTotal: 'Total', cancel: 'Cancel',
  saveOrder: 'Confirm', editOrder: 'Edit order', search: 'Search…',
  amountPaid: 'Amount collected', fullyPaid: 'Paid in full', partialPayment: 'Partial payment',
  note: 'Note',

  addProduct: 'New Product', productName: 'Name', unitPrice: 'Price (DH)',
  saveProduct: 'Save', category: 'Category', archive: 'Archive', archived: 'Archived',
  restore: 'Restore',

  registerClient: 'New Client', shopName: 'Shop name', city: 'City', sector: 'Sector',
  deliveryDays: 'Schedule', saveClient: 'Confirm', phone: 'Phone', address: 'Address',
  call: 'Call',

  orderHistory: 'History', items: 'items', orderDetails: 'Details', edit: 'Edit',
  delete: 'Delete', saveChanges: 'Save changes',
  confirmDelete: 'Permanently delete this order?',

  lifetimeTotal: 'Revenue', avgOrder: 'Average order', dailyRevenue: 'Weekly sales',
  totalClients: 'Active clients', totalOrders: 'Orders', performance: 'Performance',
  topSectors: 'Sales by sector', topProducts: 'Top products', topClientsTitle: 'Top clients',
  units: 'units', ordersLabel: 'orders', unknown: 'Unknown',

  currency: 'DH', uploadImage: 'Photo', filterBy: 'Filter by',
  noClientToday: 'No clients scheduled today.', empty: 'Nothing to show.',
  confirm: 'Confirm', close: 'Close', save: 'Save',

  exportCsv: 'Export CSV', backup: 'Backup', restoreBackup: 'Restore a backup',
  backupDone: 'Backup created.', restoreDone: 'Data restored.',
  restoreWarning: 'This will replace all data on this device. Continue?',
  language: 'Language', dataStoredLocally: 'Your data stays on this device.',

  proFeature: 'Pro feature', upgrade: 'Upgrade to Pro', upgradeNow: 'Unlock',
  trialBanner: 'Pro trial — {n} days left', trialLastDay: 'Last day of your Pro trial',
  freePlan: 'Free plan', proPlan: 'QuickDelivery Pro',
  limitReached: 'Free plan limit reached',
  limitClients: 'The free plan is limited to {n} clients.',
  limitProducts: 'The free plan is limited to {n} products.',
  limitOrders: 'The free plan is limited to {n} orders per month.',
  upgradeBlurb: 'Go Pro for unlimited use, full analytics, export and backups.',

  activation: 'Activation', licenseKey: 'Licence key', activateApp: 'Activate',
  activating: 'Activating…', activationOk: 'Licence activated. Thank you!',
  activationBadKey: 'Invalid key, or already used on too many devices.',
  activationOffline: 'First activation needs an internet connection.',
  buyLicense: 'Buy a licence', haveKey: 'I already have a key',
  deviceId: 'Device ID', licenseActive: 'Licence active', licenseUntil: 'Valid until {d}',
  licenseLifetime: 'Lifetime licence', deactivate: 'Deactivate this device',
  licenseExpired: 'Your subscription has expired. Renew to continue.',
  licenseWrongDevice: 'This licence is activated on another device.',

  byVendor: 'A SaaS product by',
};

export const translations: Record<Language, Dict> = { fr, ar, en };

/** Interpolate `{name}` placeholders. */
export function t(dict: Dict, key: keyof Dict, vars?: Record<string, string | number>): string {
  const raw = dict[key] as string;
  if (!vars) return raw;
  return raw.replace(/\{(\w+)\}/g, (m, k) => (k in vars ? String(vars[k]) : m));
}
